import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { Availability, CompleteHours, WorkRow } from '@workflow/contracts'
import { PLAYER_WORK_STATUSES } from '@workflow/contracts'
import { Button, Dialog, IconButton, Icons, ProgressBar, StatusLabel, cn } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { usePortalNotes, useUpdateMyTask } from '@/lib/api/hooks'
import { formatCreated } from '@/lib/format'
import { formatHours, hoursFigure } from '@/features/jobs/jobsStatus'
import { WritingSurface } from '@/features/writing/WritingSurface'
import { HourAdjustments, assignedText, rowState } from './workParts'

/** What the window was opened for — it decides only where the caret lands. */
export type TaskIntent = 'view' | 'hours' | 'complete' | 'note'

/**
 * One task, whole, in a window: its name and state, the minute it was
 * assigned, the studio's description, the hours as a counter
 * you can't misread, the notes on it, and a note of your own — sent on its
 * own, or with the hours or the finish it explains. The window is a page
 * you scroll on a phone and a card on a desk; every press target is a
 * thumb wide, and nothing waits behind a hover.
 */
export function TaskWindow({ row, intent, availability, onClose, onFinished, readOnly = false }: {
  row: WorkRow | null
  intent: TaskIntent
  availability: Availability
  onClose: () => void
  /** A save that logged the last hour finished the task with no note — the caller offers one. */
  onFinished?: (row: WorkRow) => void
  /** History: the details to read, with no counter to move and no note to write. */
  readOnly?: boolean
}) {
  return (
    <Dialog open={row !== null} onClose={onClose} labelledBy="task-window-title" className="sm:max-w-xl">
      {row ? <TaskBody key={row.assignmentId} row={row} intent={intent} availability={availability} onClose={onClose} onFinished={onFinished} readOnly={readOnly} /> : null}
    </Dialog>
  )
}

function TaskBody({ row, intent, availability, onClose, onFinished, readOnly = false }: {
  row: WorkRow
  intent: TaskIntent
  availability: Availability
  onClose: () => void
  onFinished?: (row: WorkRow) => void
  readOnly?: boolean
}) {
  const update = useUpdateMyTask()
  const notes = usePortalNotes()
  const recorded = row.actualHours ?? 0
  const limit = row.estimatedHours
  const [hours, setHours] = useState(recorded)
  const [note, setNote] = useState('')
  const [confirming, setConfirming] = useState(false)
  const noteArea = useRef<HTMLTextAreaElement | null>(null)
  const hoursRef = useRef<HTMLDivElement | null>(null)

  const working = PLAYER_WORK_STATUSES.includes(row.jobStatus)
  // A part nobody has begun is taken up only on the clock — the server's rule, said before it refuses.
  const waitingForClock = working && row.completion === 'assigned' && availability === 'inactive'
  // A finished task still takes a note; its hours and its finish are settled.
  const finished = row.completion === 'completed'
  const canWork = working && !waitingForClock && !finished && !readOnly
  const dirty = hours !== recorded
  const trimmed = note.trim()
  const state = rowState(row)
  const thread = (notes.data?.notes ?? []).filter((entry) => entry.jobId === row.jobId)

  useEffect(() => {
    // The caret goes where the press meant: the note for a note or a finish, the counter for hours.
    const timer = setTimeout(() => {
      if (intent === 'note' || intent === 'complete') noteArea.current?.focus()
      if (intent === 'hours') hoursRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus()
    }, 120)
    return () => clearTimeout(timer)
  }, [intent])

  // Logging the last hour of the allocation finishes the task on the server —
  // the same rule as Mark As Complete, reached by the counter.
  const reachesLimit = dirty && canWork && limit !== null && limit > 0 && hours === limit

  const send = (complete: CompleteHours | null) => {
    if (update.isPending) return
    const finishes = !complete && reachesLimit
    update.mutate(
      {
        id: row.assignmentId,
        ...(trimmed ? { note: trimmed } : {}),
        ...(dirty && canWork ? { hours: { hours, expectedHours: recorded } } : {}),
        ...(complete ? { complete } : {}),
        ...(finishes ? { finishes: true } : {}),
      },
      {
        // Finished by the counter with nothing said: offer the note the
        // Mark As Complete path would have asked for. With a note already
        // written, it went with the hours and there is nothing to offer.
        onSuccess: () => (finishes && !trimmed && onFinished
          ? onFinished({ ...row, completion: 'completed', actualHours: hours, completedAt: new Date().toISOString() })
          : onClose()),
      },
    )
  }
  const canSave = (dirty && canWork) || trimmed.length > 0

  const details = (
    <div className={readOnly ? 'px-6 pb-6 sm:px-8' : 'pb-5'}>
      <h2 id="task-window-title" className="wf-heading text-xl text-foreground">{row.jobTitle}</h2>
      <div className="mt-3 flex flex-wrap gap-2">
        <Chip icon={<Icons.calendar size="sm" />} label="Assigned">{assignedText(row.assignedAt)}</Chip>
        {row.clientName ? <Chip icon={<Icons.user size="sm" />} label="Client">{row.clientName}</Chip> : null}
      </div>

      {row.jobDescription ? (
        <p className="mt-5 whitespace-pre-wrap break-words text-ui leading-relaxed text-foreground">{row.jobDescription}</p>
      ) : null}

      <HoursCounter
        containerRef={hoursRef}
        hours={hours}
        recorded={recorded}
        limit={limit}
        enabled={canWork && !update.isPending}
        onChange={setHours}
        hint={
          finished ? 'Completed — the hours are final.'
            : readOnly ? null
            : !working ? 'Paused by the studio — hours are locked until it resumes.'
              : waitingForClock ? 'Clock in to start this task.'
                : reachesLimit ? 'Saving the final hour completes this task.'
                  : null
        }
      />
      <HourAdjustments assignmentId={row.assignmentId} className="mt-3" />

      {thread.length > 0 ? (
        <section className="mt-6">
          <p className="text-micro text-muted-foreground uppercase">Notes</p>
          <ul className="mt-2 flex flex-col gap-2">
            {thread.map((entry) => (
              <li key={entry.id} className="rounded-2xl bg-tint px-4 py-3">
                <p className="text-caption text-muted-foreground">{entry.authorName ?? 'The studio'} · {formatCreated(entry.createdAt)}</p>
                <p className="mt-1 whitespace-pre-wrap break-words text-ui text-foreground">{entry.body}</p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {readOnly ? null : <p className="mt-6 text-micro text-muted-foreground uppercase">Your note</p>}
    </div>
  )

  if (readOnly) {
    return (
      <div className="flex max-h-[calc(100dvh-2rem)] flex-col rounded-3xl border bg-surface shadow-modal sm:max-h-[calc(100dvh-4rem)]">
        <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-4 sm:px-8">
          <StatusLabel tone={state.tone} hue={state.hue} label={state.label} />
          <IconButton label="Close" size="sm" onClick={onClose} tooltipSide="bottom"><Icons.close /></IconButton>
        </div>
        <div className="min-h-0 overflow-y-auto">{details}</div>
      </div>
    )
  }

  return (
    <form
      className="flex max-h-[calc(100dvh-2rem)] flex-col rounded-3xl border bg-surface shadow-modal sm:max-h-[calc(100dvh-4rem)]"
      onSubmit={(event) => {
        event.preventDefault()
        if (canSave) send(null)
      }}
    >
      <div className="flex items-center justify-between gap-3 px-6 pt-5 sm:px-8">
        <StatusLabel tone={state.tone} hue={state.hue} label={state.label} />
        <IconButton label="Close" size="sm" onClick={onClose} tooltipSide="bottom"><Icons.close /></IconButton>
      </div>
      <WritingSurface
        value={note}
        onChange={setNote}
        onSubmit={() => canSave && send(null)}
        label="Note to the studio"
        placeholder="Write a note to the studio — optional…"
        maxLength={4000}
        minHeight="min-h-[4.5rem]"
        areaRef={noteArea}
        header={details}
        panels={update.error ? (
          <p key="fault" className="border-t border-hairline px-6 py-2.5 text-caption text-danger sm:px-8">{describeError(update.error)}</p>
        ) : null}
        actions={null}
      />
      {/* The decisions sit in their own row under the writing, each a full
          thumb wide on a phone, so no toolbar ever pushes one off the edge. */}
      <div className="flex gap-2 border-t border-hairline px-6 py-4 sm:justify-end sm:px-8">
        {canWork ? (
          <Button variant="secondary" icon={<Icons.complete size="sm" />} disabled={update.isPending} onClick={() => setConfirming(true)} className="flex-1 sm:flex-none">
            Mark As Complete
          </Button>
        ) : null}
        <Button type="submit" variant="primary" busy={update.isPending && !confirming} disabled={!canSave} className="flex-1 sm:flex-none">
          {finished || (trimmed && !(dirty && canWork)) ? 'Send Note' : 'Save'}
        </Button>
      </div>
      <CompleteDialog
        open={confirming}
        title={row.jobTitle}
        hours={dirty && canWork ? hours : recorded}
        limit={limit}
        withNote={trimmed.length > 0}
        busy={update.isPending}
        onConfirm={(how) => {
          setConfirming(false)
          send(how)
        }}
        onClose={() => setConfirming(false)}
      />
    </form>
  )
}

/**
 * Mark As Complete, asked properly. When the counter is short of the hours
 * assigned, finishing means one of two different things, and the player
 * says which: the whole task was done (the hours not yet logged are logged
 * now, in one go), or only the logged hours were done (the part is handed
 * in short, and the studio sees by how much). Nothing is chosen for them.
 * With no allocation, or every hour already logged, there is nothing to
 * choose and it is a plain yes.
 */
export function CompleteDialog({ open, title, hours, limit, withNote, busy, onConfirm, onClose }: {
  open: boolean
  title: string
  hours: number
  limit: number | null
  withNote: boolean
  busy: boolean
  onConfirm: (how: CompleteHours) => void
  onClose: () => void
}) {
  const [how, setHow] = useState<CompleteHours | null>(null)
  const short = limit !== null && limit > hours ? limit - hours : 0
  const choosing = short > 0
  useEffect(() => {
    if (open) setHow(null)
  }, [open])
  const note = withNote ? ' Your note goes with it.' : ''
  return (
    <Dialog open={open} onClose={onClose} labelledBy="complete-task-title" className="sm:max-w-md">
      <div className="rounded-3xl border bg-surface shadow-modal">
        <div className="px-6 pt-6 sm:px-7">
          <h2 id="complete-task-title" className="wf-heading text-lg text-foreground">Complete {title}?</h2>
          <p className="mt-1.5 text-ui text-muted-foreground">
            {choosing
              ? `You have logged ${hoursFigure(hours)} of ${hoursFigure(limit!)} hours. How much of the task did you do?`
              : `It moves to your completed work with ${formatHours(hours)} recorded.${note}`}
          </p>
        </div>
        {choosing ? (
          <div role="radiogroup" aria-label="How much of the task did you do?" className="mt-4 flex flex-col gap-2 px-6 sm:px-7">
            <Choice
              selected={how === 'assigned'}
              onSelect={() => setHow('assigned')}
              title={`The Whole Task · ${formatHours(limit!)}`}
              detail={`I finished all of it. The ${formatHours(short)} not yet logged are logged now.`}
            />
            <Choice
              selected={how === 'logged'}
              onSelect={() => setHow('logged')}
              title={`Only What I Logged · ${formatHours(hours)}`}
              detail={`Hand it in with ${formatHours(hours)}. The studio sees it came in ${formatHours(short)} short.`}
            />
            {withNote ? <p className="pt-1 text-caption text-muted-foreground">Your note goes with it.</p> : null}
          </div>
        ) : null}
        <div className="mt-6 flex gap-2 border-t border-hairline px-6 py-4 sm:justify-end sm:px-7">
          <Button variant="secondary" onClick={onClose} className="flex-1 sm:flex-none">Cancel</Button>
          <Button
            variant="primary"
            icon={<Icons.complete size="sm" />}
            busy={busy}
            disabled={choosing && how === null}
            onClick={() => onConfirm(choosing ? how! : 'logged')}
            className="flex-1 sm:flex-none"
          >
            Complete
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

/** One answer as a whole pressable card: a round mark, its title, what it means. */
function Choice({ selected, onSelect, title, detail }: { selected: boolean; onSelect: () => void; title: string; detail: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-3 rounded-2xl border px-4 py-3.5 text-left outline-none transition-colors duration-fast focus-visible:ring-1 focus-visible:ring-foreground',
        selected ? 'border-foreground bg-tint' : 'border-hairline hover:bg-tint',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border',
          selected ? 'border-foreground' : 'border-muted-foreground',
        )}
      >
        {selected ? <span className="size-2 rounded-full bg-foreground" /> : null}
      </span>
      <span className="min-w-0">
        <span className="block text-ui font-medium text-foreground">{title}</span>
        <span className="mt-0.5 block text-caption text-muted-foreground">{detail}</span>
      </span>
    </button>
  )
}

/**
 * The moment after the counter's last hour: the task is already complete,
 * and this only asks whether there is anything to tell the studio. Yes
 * opens the task again, on its note; no just closes. Nothing here undoes
 * or delays the finish.
 */
export function FinishedPrompt({ row, onWrite, onClose }: {
  row: WorkRow | null
  onWrite: () => void
  onClose: () => void
}) {
  return (
    <Dialog open={row !== null} onClose={onClose} labelledBy="finished-prompt-title" className="sm:max-w-sm">
      {row ? (
        <div className="rounded-3xl border bg-surface p-6 text-center shadow-modal">
          <span className="mx-auto flex size-12 items-center justify-center rounded-full bg-tint text-foreground" aria-hidden>
            <Icons.complete />
          </span>
          <h2 id="finished-prompt-title" className="wf-heading mt-4 text-lg text-foreground">Task completed</h2>
          <p className="mt-1.5 text-ui text-muted-foreground">
            You logged the final hour, so {row.jobTitle} is marked complete. Would you like to write a note to the studio?
          </p>
          <div className="mt-6 flex gap-2">
            <Button variant="secondary" onClick={onClose} className="flex-1">No</Button>
            <Button variant="primary" icon={<Icons.edit size="sm" />} onClick={onWrite} className="flex-1">Yes, Write a Note</Button>
          </div>
        </div>
      ) : null}
    </Dialog>
  )
}

/** One fact as a soft rounded chip: a mark, a quiet label, the value. */
function Chip({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-tint px-3 py-1.5 text-caption text-foreground">
      <span className="text-muted-foreground" aria-hidden>{icon}</span>
      <span className="sr-only">{label}: </span>
      <span className="truncate">{children}</span>
    </span>
  )
}

/**
 * The hours, as a counter no one misreads: the figure large in the middle,
 * round minus and plus on either side wide enough for a thumb, the total it
 * counts toward beneath, and a bar for how far along. Whole hours — the
 * same step the studio's sheet keeps.
 */
function HoursCounter({ containerRef, hours, recorded, limit, enabled, onChange, hint }: {
  containerRef: React.RefObject<HTMLDivElement | null>
  hours: number
  recorded: number
  limit: number | null
  enabled: boolean
  onChange: (hours: number) => void
  hint: string | null
}) {
  const max = limit ?? 10_000
  const step = 'flex size-11 items-center justify-center rounded-full bg-surface text-foreground shadow-chip outline-none transition-[transform,opacity,background-color] duration-fast active:scale-95 hover:bg-tint focus-visible:ring-1 focus-visible:ring-foreground disabled:opacity-35 disabled:shadow-none disabled:active:scale-100'
  return (
    <section ref={containerRef} className="mt-6 rounded-3xl bg-tint p-5">
      <div className="flex items-center justify-between">
        <p className="text-micro text-muted-foreground uppercase">Hours worked</p>
        {hours !== recorded ? <p className="text-caption text-muted-foreground">Was {formatHours(recorded)}</p> : null}
      </div>
      <div role="group" aria-label="Hours worked" className="mt-3 flex items-center justify-between gap-4">
        <button type="button" disabled={!enabled || hours <= 0} onClick={() => onChange(Math.max(0, hours - 1))} className={step}>
          <Icons.minus /><span className="sr-only">Subtract one hour</span>
        </button>
        <div className="text-center">
          <output aria-live="polite" className="block font-mono text-4xl leading-none tabular-nums text-foreground">{formatHours(hours)}</output>
          <span className="mt-1.5 block text-caption text-muted-foreground">{limit !== null ? `Of ${hoursFigure(limit)} Hours` : 'Hours'}</span>
        </div>
        <button type="button" disabled={!enabled || hours >= max} onClick={() => onChange(Math.min(max, hours + 1))} className={step}>
          <Icons.plus /><span className="sr-only">Add one hour</span>
        </button>
      </div>
      {limit ? <ProgressBar value={hours / limit} label={`${formatHours(hours)} of ${formatHours(limit)} hours`} className={cn('mt-4 h-1.5')} /> : null}
      {hint ? <p className="mt-3 text-center text-caption text-muted-foreground">{hint}</p> : null}
    </section>
  )
}
